import {
    type CommandBridge,
    type CommandHandler,
    type CommandPayload,
    ConnectionStatus,
} from '../CommandBridge'
import {
    createDisposer,
    createLogPrefix,
    Deferred,
    type Disposable,
    type Disposer,
    IllegalStateError,
    logDebug,
    logError,
    type LogTarget,
    logVerbose,
    logWarn,
    ReportableError,
    signal,
    toJson,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { createErrorHandler } from '@/errorHandler'
import { data, type ObservableValue } from '@amazon/vinyl-observable'

export interface RtcBridgeOptions {
    readonly host: string
    readonly roomId: string
}

/**
 * Creates a Real-Time Communication Connection using a rendezvous websocket service.
 */
export class RtcBridge implements CommandBridge, LogTarget, Disposable {
    logPrefix = createLogPrefix('RtcBridge')

    private _channel: RTCDataChannel | null = null
    private readonly channelReady = signal<RTCDataChannel>()
    private readonly message = signal<string>()
    private connectionDisposer: Disposer | null = null
    private readonly _connectionStatus = data(ConnectionStatus.DISCONNECTED)
    private readonly errorHandler = createErrorHandler(this)

    get connectionStatus(): ObservableValue<ConnectionStatus> {
        return this._connectionStatus
    }

    connect(options: RtcBridgeOptions) {
        const currentStatus = this._connectionStatus.value
        if (
            currentStatus === ConnectionStatus.CONNECTING ||
            currentStatus === ConnectionStatus.CONNECTED
        ) {
            logWarn(this, 'already connected')
            return
        }
        this._connectionStatus.value = ConnectionStatus.CONNECTING
        logDebug(this, 'connect', options)

        this.connectionDisposer = createDisposer()
        const { add } = this.connectionDisposer
        const wS = new WebSocket(options.host)
        add(() => {
            logDebug(this, 'peer connection and web socket closed')
            pC.close()
            wS.close()
        })
        const pC = new RTCPeerConnection({
            // ice servers would be to punch through NAT. We shouldn't do this
            iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        })

        const iceCandidate = new Deferred<void>()
        pC.onicecandidate = (e) => {
            if (e.candidate) return
            iceCandidate.resolve(void 0)
        }

        pC.onicegatheringstatechange = () =>
            logDebug(this, `ice gathering: ${pC.iceGatheringState}`)

        pC.onconnectionstatechange = () => logDebug(this, `Connection state: ${pC.connectionState}`)

        const roomId = options.roomId
        wS.onopen = () => {
            logDebug(this, 'websocket connected')
            wS.send(JSON.stringify({ type: 'join', roomId }))
        }

        wS.onmessage = async (e) => {
            const data = JSON.parse(e.data)
            const { type } = data
            logVerbose(this, 'onmessage:', type)

            switch (type) {
                case 'ready': {
                    logDebug(this, 'ready:', data.isOfferer)
                    if (data.isOfferer) {
                        this.channel = pC.createDataChannel('dataChannel')
                        void (async () => {
                            await pC.setLocalDescription(await pC.createOffer({ iceRestart: true }))
                            await iceCandidate
                            logDebug(this, 'sending local description')
                            // Sends offer through the web socket
                            wS.send(JSON.stringify(pC.localDescription))
                        })()
                    } else {
                        pC.ondatachannel = (e) => {
                            this.channel = e.channel
                        }
                    }
                    break
                }
                case 'offer': {
                    logDebug(this, 'offer')
                    await pC.setRemoteDescription(data)
                    const answer = await pC.createAnswer()
                    await pC.setLocalDescription(answer)
                    await iceCandidate
                    wS.send(JSON.stringify(pC.localDescription))
                    break
                }
                case 'answer': {
                    logDebug(this, 'answer')
                    await pC.setRemoteDescription(data)
                    break
                }
            }
        }

        wS.onerror = () => {
            logError(this, 'websocket error')
            this._connectionStatus.value = ConnectionStatus.ERRED
        }
    }

    disconnect() {
        logDebug(this, 'disconnect')
        if (!this.connectionDisposer) return
        this.channel = null
        this.connectionDisposer.dispose()
        this.connectionDisposer = null
    }

    /**
     * Resolves when the data channel is set and open.
     * @private
     */
    private waitForChannel(): Promise<RTCDataChannel> {
        const channel = this.channel
        if (channel && channel.readyState === 'open') return Promise.resolve(channel)
        return new Promise((resolve) => {
            this.channelReady.listen((channel) => {
                resolve(channel)
            })
        })
    }

    get channel(): RTCDataChannel | null {
        return this._channel
    }

    set channel(value: RTCDataChannel | null) {
        const oldChannel = this._channel
        if (oldChannel) {
            oldChannel.onopen = null
            oldChannel.onmessage = null
            oldChannel.onerror = null
            oldChannel.onclose = null
            oldChannel.close()
        }
        this._channel = value
        if (value) {
            logDebug(this, 'channel set')
            value.onopen = () => {
                logDebug(this, 'channel open')
                this._connectionStatus.value = ConnectionStatus.CONNECTED
                this.channelReady.dispatch(value)
            }
            value.onmessage = (e) => {
                logVerbose(this, 'channel message')
                this.message.dispatch(e.data)
            }
            value.onerror = (e) => {
                logError(this, `channel error: ${e}`)
                this._connectionStatus.value = ConnectionStatus.ERRED
            }
            value.onclose = () => {
                logDebug(this, `channel closed`)
                this.disconnect()
            }
        } else {
            this._connectionStatus.value = ConnectionStatus.DISCONNECTED
        }
    }

    listen(handler: CommandHandler): Unsubscribe {
        return this.message.listen((str) => {
            handler(JSON.parse(str))
        })
    }

    canSend(): boolean {
        return this.connectionStatus.value === ConnectionStatus.CONNECTED
    }

    send(command: CommandPayload): void {
        if (!this.canSend())
            throw new IllegalStateError(`not connected, cannot send '${command.type}'`)
        const channel = this._channel!
        logVerbose(this, 'sending', command.type)
        try {
            channel.send(JSON.stringify(command))
        } catch (error) {
            this.errorHandler(new RtcCommandError(command, error))
        }
    }

    dispose() {
        this.disconnect()
    }
}

export class RtcCommandError extends ReportableError {
    get [Symbol.toStringTag](): string {
        return 'RtcCommandError'
    }

    constructor(
        private readonly command: CommandPayload | null,
        private readonly reason: any
    ) {
        super(`RTC Command Error: ${reason?.message}`)
        Object.setPrototypeOf(this, RtcCommandError.prototype)
    }

    toJSON() {
        return {
            ...super.toJSON(),
            command: toJson(this.command),
            readon: toJson(this.reason),
        }
    }
}
